// Sex (1) + birth year (2) + month (2) + department (2, "2A"/"2B" for
// Corsica) + commune (3) + order number (3) + control key (2).
const NIR_REGEX = /^\d{5}(?:\d{2}|2A|2B)\d{6}\d{2}$/;

/**
 * Strips the spaces of the input mask and uppercases Corsica departments.
 * null and undefined pass through, so a payload field can be normalized as is.
 */
export const normalizeNir = <T extends string | null | undefined>(
  value: T,
): T =>
  (typeof value === 'string'
    ? value.replace(/\s/g, '').toUpperCase()
    : value) as T;

/**
 * Validates a French social security number (NIR) format and its control key:
 * key = 97 - (first 13 digits mod 97), with Corsica departments 2A and 2B
 * replaced by 19 and 18 for the computation. Spaces are ignored.
 */
export const isValidNir = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;

  const nir = normalizeNir(value);
  if (!NIR_REGEX.test(nir)) return false;

  const body = nir.slice(0, 13).replace('2A', '19').replace('2B', '18');
  const key = Number(nir.slice(13));

  return key === 97 - (Number(body) % 97);
};

/**
 * Formats a (possibly partial) NIR as typed, grouped like the carte Vitale:
 * "1 85 05 78 006 084 91". Characters beyond 15 are dropped.
 */
export const formatNir = (value: string): string => {
  const nir = normalizeNir(value).slice(0, 15);
  const groups = [1, 2, 2, 2, 3, 3, 2];
  const parts: string[] = [];
  let index = 0;
  for (const size of groups) {
    if (index >= nir.length) break;
    parts.push(nir.slice(index, index + size));
    index += size;
  }
  return parts.join(' ');
};
